import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { blogAPI, MAX_VIDEO_SIZE, uploadVideoFile } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import RichTextEditor from '../../components/RichTextEditor';
import './Admin.css';

const VIDEO_ACCEPT = 'video/mp4,video/webm,video/ogg,.mp4,.webm,.ogg';

const isContentEmpty = (html) => !html || !html.replace(/<[^>]*>/g, '').trim();

const emptyForm = {
  title: '',
  summary: '',
  content: '',
  category: '',
  tags: '',
  status: 'draft',
  is_featured: false,
  meta_description: '',
};

const BlogForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoPath, setVideoPath] = useState('');
  const [removeVideo, setRemoveVideo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.adminGetPost(id);
      const { video, featured_image, ...rest } = response.data;
      setForm({ ...emptyForm, ...rest });
      setVideoUrl(video || '');
      setVideoPath('');
      setRemoveVideo(false);
    } catch (err) {
      error('無法載入文章資料');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleContentChange = (value) => {
    setForm((prev) => ({ ...prev, content: value }));
  };

  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/') && !/\.(mp4|webm|ogg)$/i.test(file.name)) {
      error('請選擇影片檔案（MP4、WebM 或 OGG）');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      error('影片大小不能超過 300MB');
      e.target.value = '';
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);
    try {
      const response = await uploadVideoFile(file, 'blog', (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      });
      setVideoPath(response.object_name || '');
      setVideoUrl(response.url);
      setRemoveVideo(false);
    } catch (err) {
      error(err.response?.data?.error || err.message || '影片上傳失敗');
      console.error(err);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleVideoRemove = () => {
    setVideoPath('');
    setVideoUrl('');
    setRemoveVideo(true);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const buildPayload = () => {
    const data = {};
    Object.keys(emptyForm).forEach((key) => {
      data[key] = form[key];
    });
    if (videoPath) {
      data.video_path = videoPath;
    } else if (removeVideo) {
      data.clear_video = true;
    }
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isContentEmpty(form.content)) {
      error('請輸入文章內容');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await blogAPI.adminUpdatePost(id, payload);
        success('文章已更新');
      } else {
        await blogAPI.adminCreatePost(payload);
        success('文章已建立');
      }
      navigate('/admin/blog');
    } catch (err) {
      error(isEdit ? '更新失敗' : '建立失敗');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingPage message="載入文章資料中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>{isEdit ? '編輯文章' : '新增文章'}</h1>
      </div>

      <form className="admin-form card" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>標題 *</label>
          <input name="title" value={form.title} onChange={handleChange} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>分類</label>
            <input name="category" value={form.category || ''} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>標籤 (逗號分隔)</label>
            <input name="tags" value={form.tags || ''} onChange={handleChange} />
          </div>
        </div>

        <div className="form-group">
          <label>摘要</label>
          <textarea name="summary" value={form.summary || ''} onChange={handleChange} rows={2} />
        </div>

        <div className="form-group video-upload-group">
          <label>文章影片</label>
          <p className="form-hint">顯示於文章最上方，訪客可直接播放。亦可在內容編輯器中插入更多影片。</p>
          {videoUrl ? (
            <video className="hero-video-preview" src={videoUrl} controls playsInline />
          ) : (
            <div className="hero-video-preview-empty">尚未上傳影片</div>
          )}
          <div className="photo-upload-controls">
            <input
              ref={videoInputRef}
              type="file"
              accept={VIDEO_ACCEPT}
              onChange={handleVideoSelect}
              className="photo-file-input"
              disabled={uploadingVideo}
            />
            {uploadingVideo && (
              <p className="form-hint">
                {uploadProgress > 0 ? `上傳中… ${uploadProgress}%` : '處理中…'}
              </p>
            )}
            {videoUrl && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleVideoRemove} disabled={uploadingVideo}>
                移除影片
              </button>
            )}
            <p className="form-hint">支援 MP4、WebM、OGG，最大 300MB</p>
          </div>
        </div>

        <RichTextEditor
          label="內容 *"
          value={form.content}
          onChange={handleContentChange}
          placeholder="輸入文章內容，可使用工具列插入圖片或影片..."
          minHeight={280}
        />

        <div className="form-group">
          <label>SEO 描述</label>
          <input name="meta_description" value={form.meta_description || ''} onChange={handleChange} maxLength={160} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>狀態</label>
            <select name="status" value={form.status} onChange={handleChange}>
              <option value="draft">草稿</option>
              <option value="published">已發布</option>
            </select>
          </div>
          <div className="form-group checkbox-group">
            <label>
              <input name="is_featured" type="checkbox" checked={form.is_featured} onChange={handleChange} />
              精選文章
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/blog')}>
            取消
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || uploadingVideo}>
            {saving ? '儲存中...' : uploadingVideo ? '影片上傳中...' : '儲存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
